import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, X, MapPin, Stethoscope } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { DoctorPortrait } from "@/components/DoctorPortrait";
import { Button } from "@/components/ui/button";
import { specialties, locations } from "@/lib/healic-data";
import { useMergedDoctors } from "@/lib/use-merged-doctors";
import { doctorProfileSlug } from "@/lib/old-doctor-slugs";
import { listPublishedDoctors } from "@/lib/doctors.functions";

export const Route = createFileRoute("/doctors/")({
  loader: async () => {
    try {
      return await listPublishedDoctors();
    } catch (error) {
      console.error("[doctors-page] Falling back to the local doctor directory", error);
      return [];
    }
  },
  head: () => ({
    meta: [
      { title: "Meet Our Doctors — Healic Multispeciality Hospital" },
      {
        name: "description",
        content:
          "Browse experienced surgeons, gynaecologists, orthopaedic, ENT, urology and physician specialists at Healic across Gurugram, Ghaziabad, Haridwar and Agra.",
      },
      { property: "og:title", content: "Healic Doctors & Specialists" },
      {
        property: "og:description",
        content: "100+ expert specialists providing the best care across every department.",
      },
      { property: "og:url", content: "https://healic.in/doctors" },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/doctors" }],
  }),
  component: DoctorsPage,
});

function DoctorsPage() {
  const [query, setQuery] = useState("");
  const [specialtySlug, setSpecialtySlug] = useState<string>("all");
  const [locationSlug, setLocationSlug] = useState<string>("all");
  const [maxFee, setMaxFee] = useState<string>("all");

  const initialDoctors = Route.useLoaderData();
  // The loader already provides the complete directory. Avoid a redundant
  // hydration-time server-function request, which can make an otherwise
  // healthy page fail when an older cached client bundle is still active.
  const allDoctors = useMergedDoctors(false, initialDoctors);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allDoctors.filter((d) => {
      if (specialtySlug !== "all" && d.specialtySlug !== specialtySlug) return false;
      if (locationSlug !== "all" && d.locationSlug !== locationSlug) return false;
      const fee = d.consultFeeInhospital ?? d.consultFeeOnline;
      if (maxFee !== "all" && (fee == null || fee > Number(maxFee))) return false;
      if (q) {
        const specialty = specialties.find((item) => item.slug === d.specialtySlug);
        const expertise = specialty?.treatments.join(" ") ?? "";
        const aliases = d.specialtySlug === "general-surgery" ? "piles hemorrhoids fissure fistula surgeon" : "";
        const haystack = `${d.name} ${d.specialty} ${d.hospital ?? ""} ${expertise} ${aliases}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allDoctors, query, specialtySlug, locationSlug, maxFee]);

  const hasFilters = query !== "" || specialtySlug !== "all" || locationSlug !== "all" || maxFee !== "all";

  return (
    <PageShell>
      <div className="bg-muted/40">
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-20">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">Our team</p>
          <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Meet our expert doctors</h1>
          <p className="mt-4 max-w-2xl opacity-85">
            Experienced, compassionate and dedicated specialists providing the best care across every
            department at Healic.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-10 lg:px-8">
        <div className="rounded-3xl border border-border bg-card p-4 shadow-soft sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[1.4fr_1fr_1fr_1fr_auto]">
            <label className="relative flex items-center">
              <Search className="absolute left-4 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search piles, doctor or specialty…"
                className="w-full rounded-pill border border-border bg-background py-3 pl-11 pr-4 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                aria-label="Search doctors"
              />
            </label>

            <div className="relative">
              <Stethoscope className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <select
                value={specialtySlug}
                onChange={(e) => setSpecialtySlug(e.target.value)}
                className="w-full appearance-none rounded-pill border border-border bg-background py-3 pl-11 pr-4 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                aria-label="Filter by specialty"
              >
                <option value="all">All specialties</option>
                {specialties.map((sp) => (
                  <option key={sp.slug} value={sp.slug}>
                    {sp.name}
                  </option>
                ))}
                <option value="general-physician">General Physician</option>
              </select>
            </div>

            <div className="relative">
              <MapPin className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <select
                value={locationSlug}
                onChange={(e) => setLocationSlug(e.target.value)}
                className="w-full appearance-none rounded-pill border border-border bg-background py-3 pl-11 pr-4 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                aria-label="Filter by location"
              >
                <option value="all">All locations</option>
                {locations.map((loc) => (
                  <option key={loc.slug} value={loc.slug}>
                    {loc.city}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">₹</span>
              <select
                value={maxFee}
                onChange={(e) => setMaxFee(e.target.value)}
                className="w-full appearance-none rounded-pill border border-border bg-background py-3 pl-10 pr-4 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                aria-label="Filter by consultation fee"
              >
                <option value="all">Any consultation fee</option>
                <option value="500">Up to ₹500</option>
                <option value="700">Up to ₹700</option>
                <option value="1000">Up to ₹1,000</option>
                <option value="1500">Up to ₹1,500</option>
              </select>
            </div>

            {hasFilters && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setQuery("");
                  setSpecialtySlug("all");
                  setLocationSlug("all");
                  setMaxFee("all");
                }}
                className="h-auto rounded-pill px-4 py-3"
              >
                <X className="h-4 w-4" /> Clear
              </Button>
            )}
          </div>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> of{" "}
          {allDoctors.length} doctors
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 pb-16 lg:px-8">
        {filtered.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border bg-card p-12 text-center">
            <h2 className="font-display text-xl font-semibold">No doctors match your filters</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Try clearing filters or searching with a different term.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((d) => {
              const sp = specialties.find((s) => s.slug === d.specialtySlug);
              const loc = locations.find((l) => l.slug === d.locationSlug);
              const profileSlug = doctorProfileSlug(d.slug, d.name);
              return (
                <article
                  key={d.slug}
                  className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition hover:shadow-card"
                >
                  <div className="relative">
                    <DoctorPortrait
                      doctor={d}
                      className="aspect-[4/5] overflow-hidden"
                      imgClassName="transition duration-500 group-hover:scale-[1.03]"
                    />
                    {sp && (
                      <Link
                        to="/specialties/$slug"
                        params={{ slug: sp.slug }}
                        className="absolute left-2 top-2 rounded-pill bg-background/90 px-2 py-0.5 text-[9px] font-semibold text-primary shadow-sm hover:bg-background sm:left-3 sm:top-3 sm:px-3 sm:py-1 sm:text-[11px]"
                      >
                        {sp.name}
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-3 sm:p-5">
                    <Link
                      to="/doctors/$slug"
                      params={{ slug: profileSlug }}
                      className="font-display text-sm font-semibold leading-tight hover:text-accent sm:text-lg"
                    >
                      {d.name}
                    </Link>
                    <p className="mt-1 text-[11px] leading-snug text-accent sm:text-sm">{d.specialty}</p>
                    {d.experience && (
                      <p className="mt-1.5 text-[11px] text-muted-foreground sm:mt-2 sm:text-xs">{d.experience} experience</p>
                    )}
                    {loc && (
                      <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground sm:text-xs">
                        <MapPin className="h-3 w-3 shrink-0" /> Healic {loc.city}
                      </p>
                    )}
                    {(d.consultFeeInhospital != null || d.consultFeeOnline != null) && (
                      <p className="mt-1 text-[11px] font-semibold text-foreground sm:text-xs">
                        Consultation from ₹{Math.min(...[d.consultFeeInhospital, d.consultFeeOnline].filter((fee): fee is number => fee != null)).toLocaleString("en-IN")}
                      </p>
                    )}
                    <Link
                      to="/doctors/$slug"
                      params={{ slug: profileSlug }}
                      className="mt-3 inline-flex w-full items-center justify-center rounded-pill bg-cream px-3 py-2 text-[11px] font-semibold text-foreground hover:bg-accent hover:text-accent-foreground sm:mt-4 sm:px-4 sm:text-xs"
                    >
                      View profile
                    </Link>
                  </div>
                </article>

              );
            })}
          </div>
        )}
      </section>
      </div>
    </PageShell>
  );
}
